import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { FiPlus, FiTrash2, FiSave, FiMove, FiMenu, FiLink } from "react-icons/fi";
import toast from "react-hot-toast";

const DEFAULT_LINKS = [
    { label: "Shop", path: "/shop" },
    { label: "News", path: "/news" },
    { label: "Bookstores", path: "/local-bookstores" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
];

export default function AdminNavbar() {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "navigation"));
                if (snap.exists() && snap.data().links) {
                    setLinks(snap.data().links);
                } else {
                    setLinks(DEFAULT_LINKS);
                }
            } catch (err) {
                console.error("Error loading navigation:", err);
                toast.error("Failed to load navigation.");
            }
            setLoading(false);
        }
        load();
    }, []);

    function handleAdd() {
        setLinks([...links, { label: "New Link", path: "/" }]);
    }

    function handleRemove(index) {
        setLinks(links.filter((_, i) => i !== index));
    }

    function handleChange(index, field, value) {
        const newLinks = [...links];
        newLinks[index][field] = value;
        setLinks(newLinks);
    }

    function move(index, direction) {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= links.length) return;
        const newLinks = [...links];
        const temp = newLinks[index];
        newLinks[index] = newLinks[newIndex];
        newLinks[newIndex] = temp;
        setLinks(newLinks);
    }

    async function handleSave() {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "navigation"), {
                links,
                updatedAt: serverTimestamp()
            }, { merge: true });
            toast.success("Navigation updated!");
        } catch (err) {
            console.error("Save error:", err);
            toast.error("Failed to save navigation.");
        }
        setSaving(false);
    }

    if (loading) return <div className="admin-loading">Loading navigation settings...</div>;

    return (
        <div className="admin-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
                <div>
                    <h2 style={{ margin: 0 }}>Navigation Menu</h2>
                    <p style={{ color: "#888", margin: "4px 0 0", fontSize: 14 }}>Manage the main menu links in your website header.</p>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="admin-btn-secondary" onClick={handleAdd}>
                        <FiPlus /> Add Link
                    </button>
                    <button className="admin-btn-primary" onClick={handleSave} disabled={saving}>
                        <FiSave /> {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>

            <div className="admin-card" style={{ padding: 0 }}>
                <div style={{ padding: "20px 30px", borderBottom: "1px solid #eee", display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: 20, fontWeight: 700, fontSize: 13, color: "#999", textTransform: "uppercase" }}>
                    <span>Label</span>
                    <span>URL Path</span>
                    <span style={{ textAlign: "right" }}>Actions</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                    {links.map((link, index) => (
                        <div key={index} style={{ padding: "15px 30px", borderBottom: "1px solid #f8f8f8", display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: 20, alignItems: "center" }}>
                            <div style={{ position: "relative" }}>
                                <FiMenu style={{ position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)", color: "#ddd" }} />
                                <input 
                                    value={link.label} 
                                    onChange={e => handleChange(index, "label", e.target.value)} 
                                    placeholder="e.g. Shop"
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #eee" }}
                                />
                            </div>
                            <div>
                                <input 
                                    value={link.path} 
                                    onChange={e => handleChange(index, "path", e.target.value)} 
                                    placeholder="e.g. /shop"
                                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #eee", fontFamily: "monospace" }}
                                />
                            </div>
                            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                <button className="admin-action-btn" onClick={() => move(index, -1)} disabled={index === 0}><FiMove style={{ transform: "rotate(-90deg)" }} /></button>
                                <button className="admin-action-btn" onClick={() => move(index, 1)} disabled={index === links.length - 1}><FiMove style={{ transform: "rotate(90deg)" }} /></button>
                                <button className="admin-action-btn delete" onClick={() => handleRemove(index)}><FiTrash2 /></button>
                            </div>
                        </div>
                    ))}
                </div>

                {links.length === 0 && (
                    <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
                        <p>No links added. Add a link to start building your menu.</p>
                    </div>
                )}
            </div>

            <div style={{ marginTop: 30, padding: 20, background: "#fff9f0", borderRadius: 12, border: "1px solid #ffe8cc" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#856404", display: "flex", alignItems: "center", gap: 8 }}>
                    <strong>Note:</strong> Most links should start with a forward slash (e.g., <code>/shop</code>). 
                    External links should include <code>https://</code>.
                </p>
            </div>
        </div>
    );
}
